/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { DotsHorizontalIcon } from '@radix-ui/react-icons';
import { Trash2 } from 'lucide-react';
import { useSearchParams } from 'react-router';
import { mutate } from 'swr';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { useToast } from '../ui/use-toast';

import { InvitationData, IWithPagination } from '@/commons/commons';
import Pagination from '@/components/list/Pagination';
import { cn } from '@/lib/utils';
import { useGet, useMutate } from '@/net/commons';
import { useUserContext } from '@/pages/Root';

type InvitationRowActionsProps = {
    invitation: InvitationData;
    toggleRevoke: () => void;
};

const InvitationRowActions = ({ invitation, toggleRevoke }: InvitationRowActionsProps) => {
    return (
        <AlertDialog>
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex h-7 w-7 p-0 data-[state=open]:bg-muted">
                        <DotsHorizontalIcon className="h-4 w-4" />
                        <span className="sr-only">Open menu</span>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[160px]">
                    <AlertDialogTrigger asChild>
                        <DropdownMenuItem className="text-destructive">
                            <div className="flex w-full justify-between items-center">
                                Revoke invitation
                                <Trash2 size={14} />
                            </div>
                        </DropdownMenuItem>
                    </AlertDialogTrigger>
                </DropdownMenuContent>
            </DropdownMenu>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Revoke invitation</AlertDialogTitle>
                    <AlertDialogDescription>
                        Are you sure you want to revoke the invitation for{' '}
                        <span className="font-semibold text-black">{invitation.email}</span>?
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={toggleRevoke}>
                        Revoke
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

export default function InvitationsTable() {
    const [searchParams] = useSearchParams();
    const page = Number(searchParams.get('page')) || 1;
    const pageSize = Number(searchParams.get('pageSize')) || 20;
    const { toast } = useToast();
    const { data: invitations } = useGet<IWithPagination<InvitationData>>(
        `/bff/organisation/invitations?page=${page}&pageSize=${pageSize}`,
    );
    const { trigger: deleteInvite } = useMutate('/bff/organisation/invitations/:id', {
        method: 'DELETE',
    });
    const { isAdmin } = useUserContext().user;

    const shouldShowPagination = (invitations?.pagination?.total || 0) > pageSize;

    const onRevoke = async (id: number) => {
        try {
            await deleteInvite({ idParam: id });
            mutate((key) => typeof key === 'string' && key.startsWith('/bff/organisation/invitations'), undefined, {
                revalidate: true,
            });
        } catch (error) {
            toast({
                title: 'Failed to revoke invitation',
                description: 'Please try again',
                variant: 'destructive',
            });
        }
    };

    return (
        <>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[30%]">Email</TableHead>
                        <TableHead className="w-[15%]">Invited</TableHead>
                        <TableHead className="w-[15%]">Expiry</TableHead>
                        <TableHead className="w-[15%]">Role</TableHead>
                        <TableHead className="w-[15%]">Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {invitations?.data.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={6} className="text-center text-muted-foreground">
                                There are no items to display.
                            </TableCell>
                        </TableRow>
                    )}
                    {invitations?.data.map((row) => (
                        <TableRow key={row.id}>
                            <TableCell className="text-sm font-medium">{row.email}</TableCell>
                            <TableCell className="text-xs">{new Date(row.createdAt).toLocaleDateString()}</TableCell>
                            <TableCell className="text-xs">
                                {row.expiresAt ? new Date(row.expiresAt).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline" className="capitalize">
                                    {row.role}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <span
                                    className={cn(
                                        'rounded-md border px-2.5 py-0.5 uppercase text-xs font-medium',
                                        row.status === 'accepted' && 'text-green-500 border-green-500',
                                        row.status === 'pending' && 'text-orange-500 border-orange-500',
                                    )}
                                >
                                    {row.status}
                                </span>
                            </TableCell>
                            <TableCell className="flex justify-end">
                                {row.status === 'pending' && isAdmin && (
                                    <InvitationRowActions invitation={row} toggleRevoke={() => onRevoke(row.id)} />
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {invitations?.pagination && shouldShowPagination && <Pagination {...invitations.pagination} />}
        </>
    );
}
